import { reactive, toRaw } from 'vue';
import axios from 'axios';
import { ApplicationConfiguration } from '@/types'; // Adjust the import path as necessary
import { DeploymentPrefix } from '@/shared/models/DeploymentPrefix';

interface ApplicationConfigState {
  configData: ApplicationConfiguration | null;
  loaded: boolean;
  loadingPromise: Promise<void> | null;
}

const state: ApplicationConfigState = reactive({
  configData: null,
  loaded: false,
  loadingPromise: null,
});

const loadApplicationConfig = async (): Promise<void> => {
  if (!state.loaded && !state.loadingPromise) {

    let domain = getApplicationDomain();
    const endpoint = `https://api.${domain}/v3/app/configuration`;

    const mode = import.meta.env.MODE; // Vite's way to access the current mode (development/production)


    state.loadingPromise = axios.get<ApplicationConfiguration>(endpoint)
      .then(response => {
        state.configData = response.data;
        state.loaded = true;
      })
      .catch(async error => {
        if (mode == "development") {
          console.error('Failed to load config from endpoint, falling back to file', error);
          const configPath = `/src/config/config.${mode}.json`;
          try {
            const response = await fetch(configPath);
            const appConfig: ApplicationConfiguration = await response.json();
            console.warn('Loaded appConfig from file. This should only happen on localhost runs');
            console.warn(appConfig);
            state.configData = appConfig;
            state.loaded = true;
          } catch (fileError) {
            console.error('Failed to load config from file', fileError);
          }
        }
        else {
          throw Error("Error getting application configuration.");
        }
      })
      .finally(() => {
        state.loadingPromise = null;
      });
  }

  return state.loadingPromise || Promise.resolve();
};

// Allow local domain override via VITE_DOMAIN_OVERRIDE in .env.local (gitignored).
// This avoids hardcoding a target domain and accidentally committing it.
// e.g. VITE_DOMAIN_OVERRIDE=v3.alpha.aplos-nca.com
// Only applies in development mode — tests use the default domain.
const getApplicationDomain = (): string => {
  const domainOverride = import.meta.env.MODE === 'development'
    ? import.meta.env.VITE_DOMAIN_OVERRIDE
    : undefined;
  if (domainOverride) {
    return domainOverride;
  }
  let domain = window.location.hostname;
  if (domain === 'localhost') {
    domain = 'dev.aplos-nca.com';
  }
  return domain;
}

const BASE_DOMAIN = "aplos-nca.com";

/**
 * Extracts the deployment prefix from the domain by stripping the base domain.
 * e.g. "v3.alpha.aplos-nca.com" → "v3.alpha"
 *      "dev.aplos-nca.com" → "dev"
 *      "app.aplos-nca.com" → "app"
 */
const extractDeploymentPrefix = (domain: string): string => {
  const suffix = `.${BASE_DOMAIN}`;
  if (domain.endsWith(suffix)) {
    return domain.slice(0, -suffix.length);
  }
  // Fallback: if domain doesn't match expected pattern, use first segment
  return domain.split('.')[0];
}

const getApplicationDeploymentPrefix = (): DeploymentPrefix => {
  const domain = getApplicationDomain().trim();
  const prefix = extractDeploymentPrefix(domain);

  // Normalize: strip leading "v3." version prefix to get the environment name
  const normalized = prefix.replace(/^v\d+\./, '');

  switch (normalized) {
    case "alpha":
      return DeploymentPrefix.Alpha;
    case "localhost":
    case "dev":
      return DeploymentPrefix.Dev;
    case "demo":
    case "uat":
      return DeploymentPrefix.Demo;
    case "teva":
      return DeploymentPrefix.Tenant002;
    case "prod":
    case "app":
      return DeploymentPrefix.Prod;
    default:
      console.warn(`Unknown deployment prefix "${prefix}", defaulting to Prod`);
      return DeploymentPrefix.Prod;
  }
}

const getApplicationConfigData = (): ApplicationConfiguration | null => {
  const raw = toRaw(state.configData);
  return raw;
};

export const default_max_file_size_in_mb = 50

/**
 * Gets the maximum file upload size in bytes.
 * Automatically loads config if not already loaded.
 * Returns the configured size from API (uploads.max_file_size_in_mb) or falls back to default (50 MB).
 * @returns Promise<number> - Max file size in bytes
 */
const getMaxFileSize = async (): Promise<number> => {
  await loadApplicationConfig();
  const appConfig = getApplicationConfigData();

  // Get max file size in MB from API config, validating it's a valid number
  const configValue = appConfig?.uploads?.max_file_size_in_mb;
  const maxFileSizeInMB = (typeof configValue === 'number' && !isNaN(configValue) && configValue > 0)
    ? configValue
    : default_max_file_size_in_mb;

  // Convert MB to bytes
  return maxFileSizeInMB * 1024 * 1024;
};

export default {
  loadApplicationConfig,
  getApplicationConfigData,
  getMaxFileSize,
  getApplicationDeploymentPrefix
};
